// src/components/Dashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import AuraTakeCard from './AuraTakeCard';
import VisionCreationModal from './VisionCreationModal';
import ArchiveVisionModal from './ArchiveVisionModal';
import OnboardingWizard from './OnboardingFlow';
import HeaderNav from './HeaderNav';
import DashboardFinancialSnapshot from './dashboard/DashboardFinancialSnapshot';
import DashboardCashflowChart from './dashboard/DashboardCashflowChart';
import DashboardVisionProgress from './dashboard/DashboardVisionProgress';
import DashboardTodayAction from './dashboard/DashboardTodayAction';
import DashboardStreak from './dashboard/DashboardStreak';
import DashboardCommunityPulse from './dashboard/DashboardCommunityPulse';
import DashboardMilestoneDelight from './dashboard/DashboardMilestoneDelight';
import VisionDetailPage from './VisionDetailPage';
import AuraRiskGymGame from './AuraRiskGymGame';
import { usePendingVisionImagePoll } from '../hooks/usePendingVisionImagePoll';
import { useUpgrade } from '../context/UpgradeContext';
import VisionLimitCTA from './upgrade/VisionLimitCTA';
import ErrorBoundary from './common/ErrorBoundary';
import AuraFocusStrip from './advice/AuraFocusStrip';


const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { permissions, openUpgrade, refreshPermissions } = useUpgrade();
  const [dashboard, setDashboard] = useState(null);
  const [visions, setVisions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedVision, setSelectedVision] = useState(null);
  const [showVisionModal, setShowVisionModal] = useState(false);
  const [visionSuggestion, setVisionSuggestion] = useState(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [showRiskGymGame, setShowRiskGymGame] = useState(false);

  const accountState = dashboard?.account_state;
  const dashboardZero = accountState?.dashboard_zero === true;
  const financialContext = dashboard?.financial_context;
  const hasVisions = dashboard?.has_visions ?? (dashboard?.visions?.length > 0);
  const isOvercommitted = dashboard?.is_overcommitted === true;
  const firstName =
    (user?.full_name || user?.email?.split('@')[0] || 'there').split(' ')[0];

  const currentHour = new Date().getHours();
  const greeting =
    currentHour < 12 ? 'Good morning' : currentHour < 18 ? 'Good afternoon' : 'Good evening';

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (user) loadDashboardData();
  }, [user?.portfolio_connected]);

  useEffect(() => {
    if (user?.onboarding_complete || user?.portfolio_connected) {
      localStorage.setItem('aura.onboarding.complete', 'true');
      setShowWizard(false);
      return;
    }
    const done = localStorage.getItem('aura.onboarding.complete');
    setShowWizard(!done);
  }, [user?.onboarding_complete, user?.portfolio_connected, user?.user_id]);

  const loadDashboardData = async () => {
    const req = async (url) => {
      try {
        return await axios.get(url, { timeout: 25000 });
      } catch (err) {
        console.error(`Dashboard request failed: ${url}`, err?.message || err);
        return null;
      }
    };
    try {
      const [dashRes, visionsRes] = await Promise.all([
        req('/api/dashboard'),
        req('/api/visions'),
      ]);

      if (dashRes?.data) {
        setDashboard(dashRes.data);
      }

      const activeVisions = visionsRes?.data?.visions || [];
      setVisions(activeVisions);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshVisions = async () => {
    try {
      const activeRes = await axios.get('/api/visions');
      setVisions(activeRes.data.visions || []);
      await loadDashboardData();
    } catch (e) {
      console.error('Failed to refresh visions:', e);
    }
  };

  const handleVisionCreated = async () => {
    await Promise.all([loadDashboardData(), refreshPermissions()]);
    setShowVisionModal(false);
    setVisionSuggestion(null);
  };

  usePendingVisionImagePoll(visions, setVisions);

  useEffect(() => {
    if (!selectedVision?.vision_id) return;
    const fresh = visions.find((v) => v.vision_id === selectedVision.vision_id);
    if (fresh) setSelectedVision(fresh);
  }, [visions, selectedVision?.vision_id]);

  const handleConfirmArchive = async ({ note, photoUrl }) => {
    if (!archiveTarget) return;
    try {
      await axios.post('/api/visions/achieve', {
        vision_id: archiveTarget.vision_id,
        note: note || '',
        photo_url: photoUrl || null,
      });
      setShowArchiveModal(false);
      setArchiveTarget(null);
      await Promise.all([refreshVisions(), refreshPermissions()]);
    } catch (e) {
      console.error('Failed to archive vision:', e);
    }
  };

  const tryOpenVisionModal = () => {
    if (permissions?.at_vision_limit) {
      openUpgrade('vision_limit');
      return;
    }
    setShowVisionModal(true);
  };

  const goToVisions = () => navigate('/visions');

  const handleVisionSelect = (stripVision) => {
    const full = visions.find((v) => v.vision_id === stripVision.vision_id);
    if (full) {
      setSelectedVision(full);
      return;
    }
    setSelectedVision({
      vision_id: stripVision.vision_id,
      title: stripVision.name,
      target_amount: stripVision.target_amount,
      current_amount: stripVision.current_saved,
    });
  };

  const handleBackToVisions = () => {
    setSelectedVision(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 mx-auto mb-4 border-2 border-hairline border-t-pine rounded-full animate-spin" />
          <p className="text-body text-ink-muted">Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  if (selectedVision) {
    return (
      <VisionDetailPage
        vision={selectedVision}
        onBack={handleBackToVisions}
        user={user}
        onVisionUpdate={loadDashboardData}
      />
    );
  }

  return (
    <div className="min-h-screen bg-bone">
      <HeaderNav
        activeSection="dashboard"
        onSectionChange={() => {}}
        user={user}
        onLogout={onLogout}
        visionCount={visions.length}
      />

      <DashboardMilestoneDelight visions={dashboard?.visions || []} streak={dashboard?.streak} />

      <header className="px-4 md:px-8 pt-8 md:pt-10 pb-4">
        <div className="max-w-6xl mx-auto">
          <p className="text-label text-secondary mb-1">{greeting}</p>
          <h1 className="font-display text-h1 text-primary">{firstName}</h1>
        </div>
      </header>

      <main data-tour="aura-main" className="px-4 md:px-8 pb-20">
        <div className="max-w-6xl mx-auto">
          <AuraFocusStrip className="mb-5" />
          <ErrorBoundary name="Dashboard">
            <div className="dashboard-bento grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
              <div className="md:col-span-8 lg:col-span-7">
                <DashboardFinancialSnapshot
                  financialContext={financialContext}
                  dataSourceLabel={dashboard?.data_source_label}
                  dashboardZero={dashboardZero}
                  onReviewVisions={goToVisions}
                  animate={!loading}
                />
              </div>

              <div className="md:col-span-4 lg:col-span-5 flex flex-col gap-4">
                <AuraTakeCard embedded />
                {hasVisions && !isOvercommitted && (
                  <DashboardStreak streak={dashboard?.streak} />
                )}
              </div>

              <div className="md:col-span-12">
                <DashboardVisionProgress
                  visions={dashboard?.visions}
                  onCreateVision={tryOpenVisionModal}
                  onVisionSelect={handleVisionSelect}
                  atVisionLimit={permissions?.at_vision_limit}
                  permissions={permissions}
                  onUpgrade={() => openUpgrade('vision_limit')}
                  animate={!loading}
                />
              </div>

              {hasVisions && (
                <>
                  <div className="md:col-span-7 lg:col-span-8">
                    <div className="bento-card h-full">
                      <DashboardCashflowChart monthlyChart={dashboard?.monthly_chart} />
                    </div>
                  </div>

                  <div className="md:col-span-5 lg:col-span-4 flex flex-col gap-4">
                    <DashboardTodayAction
                      todayAction={dashboard?.today_action}
                      isOvercommitted={isOvercommitted}
                      onReviewVisions={goToVisions}
                      onActionDone={loadDashboardData}
                      embedded
                    />
                    <DashboardCommunityPulse pulse={dashboard?.community_pulse} embedded />
                  </div>
                </>
              )}
            </div>
          </ErrorBoundary>
        </div>
      </main>

      {visionSuggestion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="surface-panel p-6 max-w-md w-full">
            <h3 className="font-display text-h3 text-ink mb-2">Vision suggestion</h3>
            <p className="text-body text-ink mb-6">{visionSuggestion.message}</p>
            {permissions?.at_vision_limit ? (
              <VisionLimitCTA
                onUpgrade={() => {
                  openUpgrade('vision_limit');
                  setVisionSuggestion(null);
                }}
                compact
              />
            ) : (
              <div className="flex gap-3">
                <button type="button" onClick={() => setVisionSuggestion(null)} className="btn-secondary flex-1">
                  Maybe later
                </button>
                <button
                  type="button"
                  onClick={() => {
                    tryOpenVisionModal();
                    setVisionSuggestion(null);
                  }}
                  className="btn-primary flex-1"
                >
                  Create vision
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showVisionModal && (
        <VisionCreationModal
          onClose={() => setShowVisionModal(false)}
          onVisionCreated={handleVisionCreated}
          suggestionGoal={visionSuggestion?.goal}
        />
      )}

      {showArchiveModal && archiveTarget && (
        <ArchiveVisionModal
          vision={archiveTarget}
          onClose={() => setShowArchiveModal(false)}
          onConfirm={handleConfirmArchive}
        />
      )}

      {showWizard && user && (
        <div className="fixed inset-0 z-[100] bg-bone overflow-y-auto">
          <OnboardingWizard
            user={user}
            onComplete={() => {
              setShowWizard(false);
              localStorage.setItem('aura.onboarding.complete', 'true');
            }}
          />
        </div>
      )}

      {showRiskGymGame && (
        <AuraRiskGymGame user={user} onClose={() => setShowRiskGymGame(false)} />
      )}
    </div>
  );
};

export default Dashboard;
